import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  Calculator,
  CloudUpload,
  ExternalLink,
  FilePlus,
  FileText,
  Layers,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { ProductAutocomplete } from '../components/product-autocomplete'
import { MobileBackTitle } from '../components/shell/mobile-back-title'
import { MobileTopCenter } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { Dialog } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { TableShell } from '../components/ui/table-shell'
import {
  api,
  type BuilderEntry,
  type CreateInvoicePayload,
  type InvoicePreview,
  type InvoicePreviewLine,
  type Product,
} from '../lib/api'
import { formatCurrency, formatNumber } from '../lib/format'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/new',
  component: NewInvoicePage,
})

type GroupBy = 'none' | 'project' | 'user'

function todayMinus(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

function NewInvoicePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data: customersData } = useQuery({
    queryKey: ['customers', { archived: false }],
    queryFn: () => api.listCustomers(false),
    enabled: !!meData?.user,
  })
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
    enabled: !!meData?.user,
  })
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
    enabled: !!meData?.user,
  })

  // Filter state
  const [customerId, setCustomerId] = useState<string>('')
  const [projectIds, setProjectIds] = useState<string[]>([])
  const [userIds, setUserIds] = useState<string[]>([])
  const [from, setFrom] = useState<string>(todayMinus(30))
  const [to, setTo] = useState<string>(new Date().toISOString().slice(0, 10))
  const [excludeBilled, setExcludeBilled] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [groupBy, setGroupBy] = useState<GroupBy>('none')
  const [taxRate, setTaxRate] = useState<number>(19)
  const [step, setStep] = useState<'filter' | 'preview'>('filter')

  // Form fields for the actual invoice
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10))
  const [dueDate, setDueDate] = useState('')
  const [notes, setNotes] = useState('')
  const [previewData, setPreviewData] = useState<InvoicePreview | null>(null)
  const [emptyDialogOpen, setEmptyDialogOpen] = useState(false)
  const [emptyDialogCustomer, setEmptyDialogCustomer] = useState('')

  // Available projects for the chosen customer
  const allProjects = projectsData?.projects ?? []
  const filteredProjects = useMemo(
    () => (customerId ? allProjects.filter((p) => p.customerId === customerId) : allProjects),
    [allProjects, customerId],
  )

  // Pre-fill invoice number when entering preview step
  useEffect(() => {
    if (step === 'preview' && !invoiceNumber) {
      api
        .nextInvoiceNumber()
        .then((r) => setInvoiceNumber(r.invoiceNumber))
        .catch(() => {})
    }
  }, [step, invoiceNumber])

  const entriesQuery = useQuery({
    queryKey: [
      'invoice-builder-entries',
      { customerId, projectIds, userIds, from, to, excludeBilled },
    ],
    queryFn: () =>
      api.invoiceBuilderEntries({
        customerId: customerId || undefined,
        projectIds: projectIds.length ? projectIds : undefined,
        userIds: userIds.length ? userIds : undefined,
        from: from || undefined,
        to: to || undefined,
        excludeBilled,
      }),
    enabled: !!meData?.user && step === 'filter',
  })

  const previewMutation = useMutation({
    mutationFn: (input: { entryIds: string[]; taxRatePercent: number; groupBy: GroupBy }) =>
      api.invoicePreview(input),
    onSuccess: (r) => {
      setPreviewData(r.preview)
      setStep('preview')
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const createMutation = useMutation({
    mutationFn: (payload: CreateInvoicePayload) => api.createInvoice(payload),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      notify.success('Rechnung erstellt')
      navigate({ to: '/invoices/$id', params: { id: r.invoice.id } })
    },
    onError: (e: Error) => notify.error(e.message),
  })

  // Fastbill: whether the integration is configured (drives the button's
  // enabled state + hint). Available to any authenticated user.
  const { data: appSettingsData } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => api.getAppSettings(),
    enabled: !!meData?.user,
  })
  const fastbillConfigured = !!appSettingsData?.settings.fastbillApiKeySet
  // #1007: bei EMBERFIBU_RECHNUNGEN=exklusiv entstehen Rechnungen nur noch
  // über „Zeiten abrechnen“ (emberfibu); Schalter aus/an: unverändert.
  const { data: fibuStatus } = useQuery({
    queryKey: ['fibu-status'],
    queryFn: () => api.fibuStatus(),
    enabled: !!meData?.user,
  })

  const [fastbillResult, setFastbillResult] = useState<{ id: string; url: string | null } | null>(
    null,
  )
  const fastbillDraftMutation = useMutation({
    mutationFn: (body: Parameters<typeof api.createFastbillDraft>[0]) =>
      api.createFastbillDraft(body),
    onSuccess: (r) => {
      setFastbillResult({ id: r.fastbillInvoiceId, url: r.fastbillUrl })
      notify.success(`Fastbill-Entwurf #${r.fastbillInvoiceId} angelegt`)
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const entries = entriesQuery.data?.entries ?? []
  const allSelected = entries.length > 0 && entries.every((e) => selectedIds.has(e.id))

  function toggleEntry(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    if (allSelected) setSelectedIds(new Set())
    else setSelectedIds(new Set(entries.map((e) => e.id)))
  }

  function deriveCustomerFromEntries(): string {
    // If user did not pick a customer explicitly, infer from selected entries
    if (customerId) return customerId
    const cs = new Set<string>()
    for (const e of entries) {
      if (selectedIds.has(e.id) && e.customerId) cs.add(e.customerId)
    }
    if (cs.size === 1) return Array.from(cs)[0] ?? ''
    return ''
  }

  function goToPreview() {
    if (selectedIds.size === 0) {
      notify.error('Mindestens einen Eintrag auswählen.')
      return
    }
    if (!deriveCustomerFromEntries()) {
      notify.error(
        'Kein eindeutiger Kunde — entweder Kunden-Filter oben setzen oder nur Einträge eines Kunden auswählen.',
      )
      return
    }
    previewMutation.mutate({
      entryIds: Array.from(selectedIds),
      taxRatePercent: taxRate,
      groupBy,
    })
  }

  function openEmptyDialog() {
    // Pre-fill the dialog's customer with whatever's in the filter, but the
    // user can change it before confirming.
    setEmptyDialogCustomer(customerId)
    setEmptyDialogOpen(true)
  }

  function confirmEmpty() {
    if (!emptyDialogCustomer) {
      notify.error('Bitte einen Kunden wählen.')
      return
    }
    createMutation.mutate({
      customerId: emptyDialogCustomer,
      lineItems: [],
      taxRatePercent: taxRate,
    })
  }

  function createInvoice() {
    if (!previewData) return
    const cust = deriveCustomerFromEntries()
    if (!cust) {
      notify.error('Kein Kunde ermittelbar.')
      return
    }
    if (!invoiceNumber.trim()) {
      notify.error('Rechnungsnummer fehlt.')
      return
    }
    createMutation.mutate({
      invoiceNumber: invoiceNumber.trim(),
      customerId: cust,
      issueDate,
      dueDate: dueDate || null,
      periodFrom: from || null,
      periodTo: to || null,
      notes: notes || null,
      taxRatePercent: taxRate,
      currency: 'EUR',
      lineItems: previewData.lineItems.map((l) => ({
        timeEntryId: l.timeEntryId,
        productId: l.productId ?? null,
        description: l.description,
        quantity: l.quantity,
        unit: l.unit,
        unitPriceCents: l.unitPriceCents,
        userId: l.userId ?? null,
        projectId: l.projectId ?? null,
      })),
    })
  }

  function pushFastbillDraft() {
    if (!previewData) return
    const cust = deriveCustomerFromEntries()
    if (!cust) {
      notify.error('Kein Kunde ermittelbar.')
      return
    }
    if (previewData.lineItems.length === 0) {
      notify.error('Keine Positionen vorhanden.')
      return
    }
    fastbillDraftMutation.mutate({
      customerId: cust,
      notes: notes || null,
      taxRatePercent: taxRate,
      lineItems: previewData.lineItems.map((l) => ({
        description: l.description,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
      })),
    })
  }

  const totals = useMemo(() => {
    const selected = entries.filter((e) => selectedIds.has(e.id))
    const totalMin = selected.reduce((s, e) => s + e.durationMinutes, 0)
    const totalCents = selected.reduce(
      (s, e) => s + Math.round((e.durationMinutes / 60) * (e.suggestedRateCents ?? 0)),
      0,
    )
    return { totalMin, totalCents, count: selected.length }
  }, [entries, selectedIds])

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (fibuStatus?.modus === 'exklusiv') return <Navigate to="/invoices/abrechnen" />

  return (
    <div className="space-y-6 animate-fade-in pb-12">
      {/* Mobile-Shell (<640): ←+„Neue Rechnung" oben, Minimal-Bar unten
          (Builder-Aktionen bleiben im Content). Der ← geht im Vorschau-
          Schritt erst zur Auswahl zurück (wie „Zurück zur Auswahl"),
          sonst zur Rechnungsliste. */}
      <MobileTopCenter>
        <MobileBackTitle
          title="Neue Rechnung"
          onBack={() => {
            if (step === 'preview') setStep('filter')
            else navigate({ to: '/invoices' })
          }}
          backAriaLabel={step === 'preview' ? 'Zurück zur Auswahl' : 'Zurück zu den Rechnungen'}
        />
      </MobileTopCenter>

      {/* Einreihiger Standard-Kopf statt Serif-Display (Ticket „obere Leiste
          einheitlich"). keep-row: der Schritt-Indikator bleibt auch mobil. */}
      <PageHeader
        title={step === 'filter' ? 'Neue Rechnung' : 'Neue Rechnung — Vorschau'}
        eyebrow="Abrechnung"
        phoneMode="keep-row"
        actions={
          <div className="flex items-center gap-2 text-sm text-text-tertiary">
            <span className={step === 'filter' ? 'text-accent font-medium' : ''}>1. Auswahl</span>
            <ArrowRight className="h-4 w-4" />
            <span className={step === 'preview' ? 'text-accent font-medium' : ''}>2. Vorschau</span>
          </div>
        }
      />

      {step === 'filter' && (
        <>
          {/* Filter bar */}
          <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label>Kunde</Label>
                <Select
                  value={customerId}
                  onChange={(e) => {
                    setCustomerId(e.target.value)
                    setProjectIds([]) // reset projects when customer changes
                  }}
                >
                  <option value="">Alle</option>
                  {(customersData?.customers ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1">
                <Label>Projekt</Label>
                <Select
                  value={projectIds[0] ?? ''}
                  onChange={(e) => setProjectIds(e.target.value ? [e.target.value] : [])}
                >
                  <option value="">Alle</option>
                  {filteredProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1">
                <Label>Mitarbeiter</Label>
                <Select
                  value={userIds[0] ?? ''}
                  onChange={(e) => setUserIds(e.target.value ? [e.target.value] : [])}
                >
                  <option value="">Alle</option>
                  {(usersData?.users ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label>Von</Label>
                  <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Bis</Label>
                  <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-border-subtle">
              <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={excludeBilled}
                  onChange={(e) => setExcludeBilled(e.target.checked)}
                  className="accent-accent h-4 w-4"
                />
                Bereits abgerechnete Einträge ausblenden
              </label>
              {/* Mobil als Label/Feld-Raster — der freie Flex quetschte das
                  Select auf ~70px („Pro Ei") und brach „MwSt %" zweizeilig. */}
              <div className="grid grid-cols-[auto_minmax(8rem,1fr)] items-center gap-2 sm:flex sm:gap-3">
                <Label className="text-xs whitespace-nowrap">Gruppierung</Label>
                <Select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                  className="h-9 py-1.5 text-sm truncate sm:w-40"
                >
                  <option value="none">Pro Eintrag</option>
                  <option value="project">Pro Projekt</option>
                  <option value="user">Pro Mitarbeiter</option>
                </Select>
                <Label className="text-xs whitespace-nowrap">MwSt %</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={taxRate}
                  onChange={(e) => setTaxRate(Number(e.target.value))}
                  className="w-20 h-9"
                />
              </div>
            </div>
          </section>

          {/* Time-entry selection table */}
          <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
            <header className="flex items-center justify-between px-4 py-3 border-b border-border-subtle bg-bg-overlay/30">
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <Layers className="h-4 w-4" />
                {entriesQuery.isLoading
                  ? 'Lade Einträge …'
                  : entries.length === 1
                    ? '1 Eintrag gefunden'
                    : `${entries.length} Einträge gefunden`}
              </div>
              <div className="flex items-center gap-4 text-sm text-text-secondary">
                <span>
                  Auswahl: <span className="font-medium text-text-primary">{totals.count}</span>
                </span>
                <span>
                  ∑ Stunden{' '}
                  <span className="font-medium text-text-primary tabular-nums">
                    {formatNumber(totals.totalMin / 60)}
                  </span>
                </span>
                <span>
                  ∑ Netto{' '}
                  <span className="font-medium text-text-primary tabular-nums">
                    {formatCurrency(totals.totalCents)}
                  </span>
                </span>
              </div>
            </header>

            {entriesQuery.isLoading ? (
              <div className="p-8 text-center text-text-secondary">Lade …</div>
            ) : entries.length === 0 ? (
              <div className="p-8 text-center text-text-secondary">
                Keine Einträge im gewählten Filter.
              </div>
            ) : (
              <TableShell className="max-h-[60vh] overflow-y-auto rounded-none border-0">
                <table className="w-full text-sm">
                  <thead className="bg-bg-overlay/40 sticky top-0">
                    <tr className="text-left text-xs uppercase tracking-wider text-text-tertiary">
                      <th className="px-3 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          className="accent-accent h-4 w-4"
                        />
                      </th>
                      <th className="px-3 py-2 font-medium">Datum</th>
                      <th className="px-3 py-2 font-medium">Mitarbeiter</th>
                      <th className="px-3 py-2 font-medium">Projekt</th>
                      <th className="px-3 py-2 font-medium">Beschreibung</th>
                      <th className="px-3 py-2 font-medium text-right">Std.</th>
                      <th className="px-3 py-2 font-medium text-right">Satz</th>
                      <th className="px-3 py-2 font-medium text-right">Summe</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {entries.map((e) => (
                      <BuilderRow
                        key={e.id}
                        entry={e}
                        selected={selectedIds.has(e.id)}
                        onToggle={() => toggleEntry(e.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </TableShell>
            )}
          </section>

          <div className="flex justify-end">
            {selectedIds.size === 0 ? (
              <Button
                onClick={openEmptyDialog}
                size="lg"
                variant="secondary"
                disabled={createMutation.isPending}
              >
                <FilePlus className="h-4 w-4" /> Leere Rechnung erstellen
              </Button>
            ) : (
              <Button onClick={goToPreview} size="lg" disabled={previewMutation.isPending}>
                <Calculator className="h-4 w-4" /> Vorschau erstellen ({selectedIds.size})
              </Button>
            )}
          </div>
        </>
      )}

      {step === 'preview' && previewData && (
        <PreviewStep
          previewData={previewData}
          invoiceNumber={invoiceNumber}
          setInvoiceNumber={setInvoiceNumber}
          issueDate={issueDate}
          setIssueDate={setIssueDate}
          dueDate={dueDate}
          setDueDate={setDueDate}
          notes={notes}
          setNotes={setNotes}
          taxRate={taxRate}
          setTaxRate={(v) => {
            setTaxRate(v)
            // Re-calc preview locally
            const subtotalCents = previewData.lineItems.reduce((s, l) => s + l.totalCents, 0)
            const taxCents = Math.round((subtotalCents * v) / 100)
            setPreviewData({
              ...previewData,
              taxRatePercent: v,
              taxCents,
              totalCents: subtotalCents + taxCents,
            })
          }}
          onLineEdit={(idx, patch) => {
            const next = [...previewData.lineItems]
            const old = next[idx]
            const merged = { ...old, ...patch }
            merged.totalCents = Math.round(merged.quantity * merged.unitPriceCents)
            next[idx] = merged
            const subtotalCents = next.reduce((s, l) => s + l.totalCents, 0)
            const taxCents = Math.round((subtotalCents * previewData.taxRatePercent) / 100)
            setPreviewData({
              ...previewData,
              lineItems: next,
              subtotalCents,
              taxCents,
              totalCents: subtotalCents + taxCents,
            })
          }}
          onLineDelete={(idx) => {
            const next = previewData.lineItems.filter((_, i) => i !== idx)
            const subtotalCents = next.reduce((s, l) => s + l.totalCents, 0)
            const taxCents = Math.round((subtotalCents * previewData.taxRatePercent) / 100)
            setPreviewData({
              ...previewData,
              lineItems: next,
              subtotalCents,
              taxCents,
              totalCents: subtotalCents + taxCents,
            })
          }}
          onLineAdd={(line) => {
            const next = [...previewData.lineItems, line]
            const subtotalCents = next.reduce((s, l) => s + l.totalCents, 0)
            const taxCents = Math.round((subtotalCents * previewData.taxRatePercent) / 100)
            setPreviewData({
              ...previewData,
              lineItems: next,
              subtotalCents,
              taxCents,
              totalCents: subtotalCents + taxCents,
            })
          }}
          onBack={() => setStep('filter')}
          onCreate={createInvoice}
          isCreating={createMutation.isPending}
          onFastbillDraft={pushFastbillDraft}
          isFastbillPending={fastbillDraftMutation.isPending}
          fastbillConfigured={fastbillConfigured}
          fastbillResult={fastbillResult}
        />
      )}

      <Dialog
        open={emptyDialogOpen}
        onClose={() => setEmptyDialogOpen(false)}
        title="Leere Rechnung erstellen"
        description="Wähle den Kunden — Rechnungsnummer, Datum und Positionen kannst du anschließend in der Detailansicht setzen."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEmptyDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={confirmEmpty}
              disabled={!emptyDialogCustomer || createMutation.isPending}
            >
              <FilePlus className="h-4 w-4" /> Anlegen
            </Button>
          </div>
        }
      >
        <div className="space-y-2">
          <Label htmlFor="empty-customer">Kunde</Label>
          <Select
            id="empty-customer"
            value={emptyDialogCustomer}
            onChange={(e) => setEmptyDialogCustomer(e.target.value)}
          >
            <option value="">— wählen —</option>
            {(customersData?.customers ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
      </Dialog>
    </div>
  )
}

function BuilderRow({
  entry,
  selected,
  onToggle,
}: {
  entry: BuilderEntry
  selected: boolean
  onToggle: () => void
}) {
  const start = new Date(entry.startTime)
  const hours = entry.durationMinutes / 60
  const lineCents = Math.round(hours * (entry.suggestedRateCents ?? 0))
  return (
    <tr
      onClick={onToggle}
      className={`cursor-pointer hover:bg-bg-overlay/30 transition-colors ${
        selected ? 'bg-accent/5' : ''
      }`}
    >
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="accent-accent h-4 w-4"
        />
      </td>
      <td className="px-3 py-2 text-text-secondary tabular-nums">
        {start.toLocaleDateString('de-DE')}
      </td>
      <td className="px-3 py-2 text-text-secondary">{entry.userName ?? '—'}</td>
      <td className="px-3 py-2 text-text-primary">
        <div className="font-medium">{entry.projectName ?? '—'}</div>
        {entry.customerName && (
          <div className="text-xs text-text-tertiary">{entry.customerName}</div>
        )}
      </td>
      <td className="px-3 py-2 text-text-secondary max-w-[24rem]">
        <span className="block truncate" title={entry.description}>
          {entry.description}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(hours)}</td>
      <td className="px-3 py-2 text-right text-text-tertiary tabular-nums">
        {entry.suggestedRateCents != null ? formatCurrency(entry.suggestedRateCents) : '—'}
      </td>
      <td className="px-3 py-2 text-right tabular-nums font-medium">{formatCurrency(lineCents)}</td>
    </tr>
  )
}

function PreviewStep({
  previewData,
  invoiceNumber,
  setInvoiceNumber,
  issueDate,
  setIssueDate,
  dueDate,
  setDueDate,
  notes,
  setNotes,
  taxRate,
  setTaxRate,
  onLineEdit,
  onLineDelete,
  onLineAdd,
  onBack,
  onCreate,
  isCreating,
  onFastbillDraft,
  isFastbillPending,
  fastbillConfigured,
  fastbillResult,
}: {
  previewData: InvoicePreview
  invoiceNumber: string
  setInvoiceNumber: (v: string) => void
  issueDate: string
  setIssueDate: (v: string) => void
  dueDate: string
  setDueDate: (v: string) => void
  notes: string
  setNotes: (v: string) => void
  taxRate: number
  setTaxRate: (v: number) => void
  onLineEdit: (
    idx: number,
    patch: Partial<{
      description: string
      quantity: number
      unitPriceCents: number
      unit: string
      productId: string | null
    }>,
  ) => void
  onLineDelete: (idx: number) => void
  onLineAdd: (line: InvoicePreviewLine) => void
  onBack: () => void
  onCreate: () => void
  isCreating: boolean
  onFastbillDraft: () => void
  isFastbillPending: boolean
  fastbillConfigured: boolean
  fastbillResult: { id: string; url: string | null } | null
}) {
  const [productPickerOpen, setProductPickerOpen] = useState(false)
  const { data: productsData } = useQuery({
    queryKey: ['products', { archived: false }],
    queryFn: () => api.listProducts(false),
    enabled: productPickerOpen,
  })
  const products = productsData?.products ?? []
  return (
    <>
      <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label>Rechnungs-Nr.</Label>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="RE-2026-0001"
            />
          </div>
          <div className="space-y-1">
            <Label>Datum</Label>
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Fällig</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>MwSt %</Label>
            <Input
              type="number"
              min="0"
              step="0.5"
              value={taxRate}
              onChange={(e) => setTaxRate(Number(e.target.value))}
            />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
        <header className="px-4 py-3 border-b border-border-subtle bg-bg-overlay/30 text-sm text-text-secondary flex items-center justify-between gap-3">
          <span>Positionen ({previewData.lineItems.length})</span>
          <Button size="sm" variant="ghost" onClick={() => setProductPickerOpen(true)}>
            <FilePlus className="h-3.5 w-3.5" /> Produkt hinzufügen
          </Button>
        </header>
        <TableShell className="rounded-none border-0">
          <table className="w-full text-sm">
            <thead className="bg-bg-overlay/20">
              <tr className="text-left text-xs uppercase tracking-wider text-text-tertiary">
                <th className="px-3 py-2 font-medium">Beschreibung</th>
                <th className="px-3 py-2 font-medium text-right">Menge</th>
                <th className="px-3 py-2 font-medium text-right">Einzelpreis</th>
                <th className="px-3 py-2 font-medium text-right">Summe</th>
                <th className="px-3 py-2 w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {previewData.lineItems.map((li, idx) => (
                <tr
                  key={li.timeEntryId ?? `${li.projectId ?? 'p'}:${li.userId ?? 'u'}:${idx}`}
                  className="hover:bg-bg-overlay/20"
                >
                  <td className="px-3 py-2">
                    <ProductAutocomplete
                      value={li.description}
                      onChange={(v) => onLineEdit(idx, { description: v })}
                      onPick={(p) => {
                        const desc = p.description?.trim() ? `${p.name} — ${p.description}` : p.name
                        onLineEdit(idx, {
                          description: desc,
                          unitPriceCents: p.defaultPriceCents,
                          unit: p.unit,
                          productId: p.id,
                        })
                      }}
                      className="bg-transparent border-b border-transparent hover:border-border-subtle focus:border-accent/60 focus:outline-none transition-colors text-text-primary"
                    />
                    <LineOriginBadge line={li} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      step="0.25"
                      value={li.quantity}
                      onChange={(e) => onLineEdit(idx, { quantity: Number(e.target.value) })}
                      className="w-20 bg-transparent border-b border-transparent hover:border-border-subtle focus:border-accent/60 focus:outline-none transition-colors text-right tabular-nums"
                    />{' '}
                    <span className="text-text-tertiary text-xs">{li.unit}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      step="0.01"
                      value={(li.unitPriceCents / 100).toFixed(2)}
                      onChange={(e) =>
                        onLineEdit(idx, {
                          unitPriceCents: Math.round(Number(e.target.value) * 100),
                        })
                      }
                      className="w-24 bg-transparent border-b border-transparent hover:border-border-subtle focus:border-accent/60 focus:outline-none transition-colors text-right tabular-nums"
                    />
                    <span className="text-text-tertiary text-xs ml-1">€</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {formatCurrency(li.totalCents)}
                  </td>
                  <td className="px-3 py-2">
                    <Button variant="ghost" size="sm" onClick={() => onLineDelete(idx)}>
                      ×
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-bg-overlay/30 border-t border-border-subtle">
              <tr>
                <td colSpan={3} className="px-3 py-2 text-right text-sm text-text-secondary">
                  Zwischensumme
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatCurrency(previewData.subtotalCents)}
                </td>
                <td />
              </tr>
              <tr>
                <td colSpan={3} className="px-3 py-2 text-right text-sm text-text-secondary">
                  MwSt {taxRate.toFixed(0)}&nbsp;%
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatCurrency(previewData.taxCents)}
                </td>
                <td />
              </tr>
              <tr>
                <td colSpan={3} className="px-3 py-2 text-right font-medium">
                  Gesamtbetrag
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-bold text-lg">
                  {formatCurrency(previewData.totalCents)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </TableShell>
      </section>

      <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4">
        <Label>Hinweise (optional)</Label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3 py-2 text-base lg:text-sm text-text-primary focus:border-accent/60 focus:outline-none focus:ring-2 focus:ring-accent/20"
          placeholder="Zahlungsziel 14 Tage netto …"
        />
      </section>

      {fastbillResult && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-success/40 bg-success/10 px-4 py-2.5 text-sm text-text-primary">
          <span>
            Fastbill-Entwurf <span className="font-medium tabular-nums">#{fastbillResult.id}</span>{' '}
            angelegt.
          </span>
          {fastbillResult.url && (
            <a
              href={fastbillResult.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
            >
              In Fastbill öffnen
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      )}

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="secondary" onClick={onBack}>
          Zurück zur Auswahl
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="lg"
            onClick={onFastbillDraft}
            disabled={
              isFastbillPending || !fastbillConfigured || previewData.lineItems.length === 0
            }
            title={
              fastbillConfigured
                ? 'Diese Rechnung als Entwurf in Fastbill anlegen'
                : 'Fastbill ist nicht konfiguriert — Einstellungen → Integrationen'
            }
          >
            <CloudUpload className="h-4 w-4" />
            {isFastbillPending ? 'Sende an Fastbill …' : 'In Fastbill als Entwurf'}
          </Button>
          <Button
            onClick={onCreate}
            size="lg"
            disabled={isCreating || previewData.lineItems.length === 0}
          >
            <FileText className="h-4 w-4" />
            {isCreating ? 'Erstelle …' : 'Rechnung erstellen'}
          </Button>
        </div>
      </div>

      <ProductPickerDialog
        open={productPickerOpen}
        products={products}
        onClose={() => setProductPickerOpen(false)}
        onPick={(p) => {
          const line: InvoicePreviewLine = {
            timeEntryId: null,
            productId: p.id,
            description: p.description?.trim() ? `${p.name} — ${p.description}` : p.name,
            quantity: 1,
            unit: p.unit,
            unitPriceCents: p.defaultPriceCents,
            totalCents: p.defaultPriceCents,
            userId: null,
            projectId: null,
          }
          onLineAdd(line)
          setProductPickerOpen(false)
        }}
      />
    </>
  )
}

// Renders a small "aus Zeiteintrag" / "aus Produkte" badge beneath the
// description input. Free-form lines (both fields null) render nothing so
// the row stays compact.
function LineOriginBadge({ line }: { line: InvoicePreviewLine }) {
  if (line.timeEntryId) {
    return <p className="mt-0.5 text-2xs text-text-tertiary">aus Zeiteintrag</p>
  }
  if (line.productId) {
    return <p className="mt-0.5 text-2xs text-accent/70">aus Produkte</p>
  }
  return null
}

function ProductPickerDialog({
  open,
  products,
  onClose,
  onPick,
}: {
  open: boolean
  products: Product[]
  onClose: () => void
  onPick: (product: Product) => void
}) {
  const [search, setSearch] = useState('')
  const q = search.trim().toLowerCase()
  const filtered = q
    ? products.filter(
        (p) =>
          p.name.toLowerCase().includes(q) || (p.description?.toLowerCase().includes(q) ?? false),
      )
    : products
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Produkt zur Rechnung hinzufügen"
      description="Aus Stammdaten – Menge + Preis kannst du danach in der Position anpassen."
      className="max-w-xl"
      footer={
        <Button variant="ghost" onClick={onClose} type="button">
          Schließen
        </Button>
      }
    >
      <div className="space-y-3 pb-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Produkt suchen…"
          autoFocus
        />
        {products.length === 0 ? (
          <p className="text-sm text-text-tertiary">
            Noch keine Produkte angelegt. Geh zu Stammdaten → Produkte und leg z.B. eine
            Lizenz-Pauschale an.
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-text-tertiary">Keine Treffer.</p>
        ) : (
          <div className="max-h-72 overflow-y-auto rounded-lg border border-border-subtle divide-y divide-border-subtle">
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onPick(p)}
                className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-bg-overlay/40 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{p.name}</p>
                  {p.description && (
                    <p className="mt-0.5 text-xs text-text-tertiary truncate">{p.description}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-mono tabular-nums text-accent">
                    {formatCurrency(p.defaultPriceCents)}
                  </p>
                  <p className="text-[11px] text-text-tertiary">/ {p.unit}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  )
}
